import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';
import { TableLazyLoadEvent } from 'primeng/table';
import { Subscription } from 'rxjs';
import { ColumnTypeEnum } from '../model/base-column/column-type-enum';
import { TableConfig } from '../model/config/table-config.model';

@Component({
  selector: 'eo-table',
  templateUrl: './eo-table.component.html',
  styleUrl: './eo-table.component.scss',
})
export class EoTableComponent<T> {
  @Input() customTemplates: { [key: string]: TemplateRef<any> | null } = {};
  @Input() lazyLoadOnInit = true;
  @Input() lazy: boolean = false;
  @Input() paginator: boolean = false;
  @Input() rows: number = 10;
  @Input() totalRecords?: number;
  @Input() loading = false;
  @Input() Config: TableConfig<T> | undefined;

  _config?: TableConfig<T>;
  @Input() set config(val: TableConfig<T> | undefined) {
    this.Config = val;
    this.setResults();
  }

  get config(): TableConfig<T> | undefined {
    return this.Config;
  }

  @Input() dataKey: string = '';
  @Input() rowData: T[] | undefined;
  @Input() currentPage: number | undefined;
  @Input() pageSize: number | undefined;
  @Input() totalCount: number | undefined;
  @Input() tableStyle: any = { 'min-width': '50rem', 'border-radius': '10px' };
  @Input() styleClass: string = 'p-datatable-sm p-datatable-striped';
  @Input() rowsPerPageOptions = [5, 10, 20, 30, 50, 100];
  @Input() isTableVisible: boolean = true;

  @Output() pageChange = new EventEmitter<any>();
  @Output() lazyLoad = new EventEmitter<any>();
  @Output() view: EventEmitter<any> = new EventEmitter();
  @Output() edit: EventEmitter<any> = new EventEmitter();

  ColumnTypeEnum = ColumnTypeEnum;

  constructor() {}
  ngOnInit() {
    if (this.rowData && this.Config) {
      const table: TableConfig<T> = {
        Title: this.Config?.Title ?? '',
        Data: this.rowData ?? [],
        Columns: this.Config?.Columns ?? [],
        LineNumbers: this.Config?.LineNumbers ?? false,
        IconButton: this.Config?.IconButton ?? [],
        Paging: this.Config?.Paging ?? false,
      };

      this.Config = table;
    }
    this.setResults();
  }

  private dataSubscription: Subscription | undefined;
  ngOnDestroy(): void {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
  }

  //lazy load
  onLazyLoad(event: TableLazyLoadEvent) {
    this.lazyLoad.emit(event);
  }

  // Pagination
  onPageChange(event: any) {
    // if (event?.page === this.currentPage && event?.items === this.pageSize)
    //   return;

    if (!this.lazy) {
      this.setCurrentPage(event.page);
      this.setPagesize(event.rows);
    }

    this.pageChange.emit(event);
  }

  setCurrentPage(value: number) {
    this.currentPage = value;
  }
  getCurrentPage() {
    return this.currentPage ?? 0;
  }

  setPagesize(value: number) {
    this.pageSize = value;
  }
  getPagesize() {
    return this.pageSize ?? 10;
  }

  private currentData: T[] = [];
  setResults() {
    if (this.Config?.Data) {
      this.currentData = this.Config?.Data;
    } else {
      this.dataSubscription = this.Config?.BehaviorData?.subscribe((data) => {
        this.currentData = data;
      });
    }
  }

  getResults() {
    if (this.Config?.Paging) {
      const currentPage = this.getCurrentPage();
      const pageSize = this.getPagesize();

      return (
        this.currentData.slice(
          currentPage * pageSize,
          (currentPage + 1) * pageSize
        ) ?? []
      );
    }
    return this.currentData ?? [];
  }

  getTotal(): number {
    const length = this.totalRecords ?? this.Config?.Data?.length ?? 0;
    return length;
  }

  getFirst() {
    if (this.currentPage && this.pageSize) {
      return this.currentPage * this.pageSize;
    }

    const currentPage = this.getCurrentPage();
    const pageSize = this.getPagesize();
    return currentPage * pageSize;
  }

  getIcon(
    icon: string | ((item?: T) => string) | undefined,
    item?: any
  ): string | undefined {
    if (typeof icon === 'function') {
      return icon(item);
    } else {
      return icon;
    }
  }

  getHeaderIcon(icon: string | (() => string) | undefined): string | undefined {
    if (typeof icon === 'function') {
      return icon();
    } else {
      return icon;
    }
  }

  getTitle(
    title: string | ((item?: T) => string) | undefined,
    item?: any
  ): string | undefined {
    if (typeof title === 'function') {
      return title(item);
    } else {
      return title;
    }
  }

  getHeaderTitle(
    title: string | (() => string) | undefined
  ): string | undefined {
    if (typeof title === 'function') {
      return title();
    } else {
      return title;
    }
  }

  getTooltip(
    tooltip: string | ((item?: any) => string) | undefined,
    item?: any
  ): string | undefined {
    if (typeof tooltip === 'function') {
      return tooltip(item);
    } else {
      return tooltip;
    }
  }

  getStyleClass(
    styleClass: string | ((item?: T) => string) | undefined,
    item?: any
  ): string | undefined {
    if (typeof styleClass === 'function') {
      return styleClass(item);
    } else {
      return styleClass;
    }
  }

  getValue(
    value: string | ((item?: T) => string) | undefined,
    item?: any
  ): string | undefined {
    if (typeof value === 'function') {
      return value(item);
    } else {
      return item;
    }
  }

  protected getLoadingColumns() {
    let colLength = this.config?.Columns?.length ?? 0;

    if (this.config?.LineNumbers) colLength++;
    return Array.from(Array(colLength).keys());
  }
}
